// TrafficAnalytics.tsx
import { useState, useEffect, useCallback } from "react";
import {
    RefreshCw, Loader2, ShieldAlert, Activity,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    ArrowUp, ArrowDown, Ban, CheckCircle2,
    Package, Database, Zap, Users, Shield, AlertTriangle,
    Info
} from "lucide-react";
import baseapi from "../../../../services/baseapi";
import { showError } from "../../../../utils/toast";

// ── Types ─────────────────────────────────────────────────────────────────────
interface QuarantineList { total: number; entries: any[]; description: string; }
interface SessionSummary { total_sessions: number; description: string; note: string; }
interface TrafficCounters {
    total_bytes: number; denied_bytes: number; allowed_bytes: number;
    total_packets: number; denied_packets: number; allowed_packets: number;
    description: string;
}
interface DosPolicyCfg {
    name: string; action: string; threshold: number; action_raw: number;
}
interface DosPolicyConfig { total: number; policies: DosPolicyCfg[]; description: string; }

interface TrafficAnalyticsData {
    quarantine_list: QuarantineList;
    session_summary: SessionSummary;
    traffic_counters: TrafficCounters;
    dos_policy_config: DosPolicyConfig;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtBytes = (b: number): string => {
    if (b >= 1e12) return `${(b / 1e12).toFixed(2)} TB`;
    if (b >= 1e9) return `${(b / 1e9).toFixed(2)} GB`;
    if (b >= 1e6) return `${(b / 1e6).toFixed(2)} MB`;
    if (b >= 1e3) return `${(b / 1e3).toFixed(2)} KB`;
    return `${b} B`;
};

const fmtNum = (n: number): string =>
    n >= 1e6 ? `${(n / 1e6).toFixed(2)}M`
        : n >= 1e3 ? `${(n / 1e3).toFixed(1)}K`
            : String(n);

// ── Stat Card ─────────────────────────────────────────────────────────────────
const StatCard = ({
    label, value, icon: Icon, bg, border, text, iconBg, sub
}: {
    label: string; value: string; icon: React.ElementType;
    bg: string; border: string; text: string; iconBg: string; sub?: string;
}) => (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${bg} ${border} shadow-sm dark:shadow-none`}>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border ${iconBg}`}>
            <Icon size={14} className={text} />
        </div>
        <div className="min-w-0 space-y-1">
            <p className="text-[11px] text-slate-600 dark:text-gray-400 tracking-widest mt-0.5">{label}</p>
            <p className={`text-[18px] font-black leading-none truncate ${text}`}>{value}</p>
            {sub && <p className="text-[8px] text-slate-400 dark:text-gray-500 mt-0.5">{sub.replace(/_/g, " ")}</p>}
        </div>
    </div>
);

// ── Section Header ────────────────────────────────────────────────────────────
const SectionHeader = ({ icon: Icon, title, color, desc }: {
    icon: React.ElementType; title: string; color: string; desc?: string;
}) => (
    <div className="flex items-center gap-2.5 mb-3">
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center border ${color}`}>
            <Icon size={13} className={color.replace("border-", "text-").replace("/30", "/80")} />
        </div>
        <div>
            <h2 className="text-[12px] font-black text-slate-900 dark:text-white uppercase tracking-widest">{title}</h2>
            {desc && <p className="text-[9px] text-slate-500 dark:text-gray-400 mt-0.5">{desc}</p>}
        </div>
    </div>
);

// ── DoS Policy Table ──────────────────────────────────────────────────────────
const PAGE_SIZE = 10;

const DosPolicyTable = ({ data }: { data: DosPolicyConfig }) => {
    const [page, setPage] = useState(1);
    const [sortConfig, setSortConfig] = useState<{ key: keyof DosPolicyCfg; dir: "asc" | "desc" } | null>(null);

    const sorted = [...data.policies].sort((a, b) => {
        if (!sortConfig) return 0;
        const av = a[sortConfig.key];
        const bv = b[sortConfig.key];
        const cmp = typeof av === "number" && typeof bv === "number"
            ? av - bv
            : String(av).localeCompare(String(bv));
        return sortConfig.dir === "asc" ? cmp : -cmp;
    });

    const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
    const pageItems = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const start = sorted.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const end = Math.min(page * PAGE_SIZE, sorted.length);

    const handleSort = (key: keyof DosPolicyCfg) => {
        setSortConfig(prev =>
            prev?.key === key
                ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
                : { key, dir: "asc" }
        );
        setPage(1);
    };

    const SortIcon = ({ col }: { col: keyof DosPolicyCfg }) => {
        if (sortConfig?.key !== col) return (
            <ArrowUp size={9} className="opacity-0 group-hover:opacity-40 text-slate-400 dark:text-slate-500 transition-opacity" />
        );
        return sortConfig.dir === "asc"
            ? <ArrowUp size={9} className="text-orange-600 dark:text-orange-400" />
            : <ArrowDown size={9} className="text-orange-600 dark:text-orange-400" />;
    };

    const Th = ({ label, col, right = false }: { label: string; col: keyof DosPolicyCfg; right?: boolean }) => (
        <th
            onClick={() => handleSort(col)}
            className={`px-4 py-2 text-[10px] font-bold text-slate-700 dark:text-gray-100 uppercase tracking-widest cursor-pointer group whitespace-nowrap ${right ? "text-right" : ""}`}
        >
            <div className={`flex items-center gap-1 ${right ? "justify-end" : ""}`}>
                {label} <SortIcon col={col} />
            </div>
        </th>
    );

    // Parse action tags
    const ActionTags = ({ action }: { action: string }) => {
        const parts = action.split("+").filter(Boolean);
        const colors: Record<string, string> = {
            alert: "bg-amber-500/10 border-amber-500/20 text-amber-600 dark:text-yellow-400",
            block: "bg-rose-500/10 border-rose-500/20 text-rose-600 dark:text-red-400",
            "rate-limit": "bg-blue-500/10 border-blue-500/20 text-blue-600 dark:text-blue-400",
        };
        return (
            <div className="flex items-center gap-1 flex-wrap">
                {parts.map(p => (
                    <span key={p} className={`px-1.5 py-0.5 text-[8px] font-bold border rounded uppercase ${colors[p] ?? "bg-slate-100 dark:bg-gray-700/40 border-slate-200 dark:border-gray-600/30 text-slate-600 dark:text-gray-400"}`}>
                        {p}
                    </span>
                ))}
            </div>
        );
    };

    return (
        <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm dark:shadow-none">
            <div className="overflow-x-auto custom-scroll">
                <table className="w-full text-left text-xs border-collapse">
                    <thead className="bg-slate-50 dark:bg-gray-800/80 sticky top-0 z-10">
                        <tr className="border-b border-slate-200 dark:border-gray-700/50">
                            <Th label="Policy Name" col="name" />
                            <Th label="Traffic Actions" col="action" />
                            <Th label="Threshold" col="threshold" right />
                        </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-100 dark:divide-gray-700/30">
                        {pageItems.length === 0 ? (
                            <tr>
                                <td colSpan={3}>
                                    <div className="flex flex-col items-center gap-2 py-16 opacity-40">
                                        <Info size={22} className="text-slate-400 dark:text-gray-400" />
                                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-gray-400">
                                            No DoS policies available
                                        </span>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            pageItems.map((p, i) => (
                                <tr key={i} className="hover:bg-slate-50 dark:hover:bg-gray-700/30 transition-colors">
                                    <td className="px-4 py-2.5 whitespace-nowrap">
                                        <div className="flex items-center gap-2">
                                            <div className="w-1.5 h-1.5 rounded-full bg-orange-500 dark:bg-orange-400 shrink-0" />
                                            <span className="text-[11px] font-bold text-slate-800 dark:text-gray-200">
                                                {p.name.replace(/_/g, " ")}
                                            </span>
                                        </div>
                                    </td>

                                    <td className="px-4 py-2.5">
                                        <ActionTags action={p.action} />
                                    </td>

                                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                                        <span className="font-mono text-[11px] font-bold text-slate-800 dark:text-gray-200">
                                            {p.threshold.toLocaleString()}
                                            <span className="text-slate-400 dark:text-gray-400 font-normal text-[9px] ml-1">pps</span>
                                        </span>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Table Pagination */}
            <div className="flex items-center justify-between px-4 py-2 border-t border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800/40">
                <span className="text-[9px] text-slate-500 dark:text-gray-400 font-medium tracking-widest">
                    <span className="text-slate-700 dark:text-gray-300 font-semibold">{start}</span> – <span className="text-slate-700 dark:text-gray-300 font-semibold">{end}</span>
                    <span className="mx-1 text-slate-400 dark:text-gray-600">/</span>
                    <span className="text-slate-700 dark:text-gray-300 font-bold">{sorted.length}</span>
                </span>
                <div className="flex items-center gap-1">
                    <button disabled={page === 1} onClick={() => setPage(1)}
                        className="p-1 text-slate-500 dark:text-gray-400 hover:text-slate-800 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 rounded border border-slate-200 dark:border-transparent transition-all">
                        <ChevronsLeft size={13} />
                    </button>
                    <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
                        className="p-1 text-slate-500 dark:text-gray-400 hover:text-slate-800 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 rounded border border-slate-200 dark:border-transparent transition-all">
                        <ChevronLeft size={13} />
                    </button>
                    <span className="px-2 py-0.5 bg-orange-500/10 border border-orange-500/20 text-orange-600 dark:text-orange-400 rounded text-[10px] font-bold mx-1">
                        {page}
                    </span>
                    <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                        className="p-1 text-slate-500 dark:text-gray-400 hover:text-slate-800 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 rounded border border-slate-200 dark:border-transparent transition-all">
                        <ChevronRight size={13} />
                    </button>
                    <button disabled={page >= totalPages} onClick={() => setPage(totalPages)}
                        className="p-1 text-slate-500 dark:text-gray-400 hover:text-slate-800 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 rounded border border-slate-200 dark:border-transparent transition-all">
                        <ChevronsRight size={13} />
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── Main Component ────────────────────────────────────────────────────────────
export default function TrafficAnalytics({ asset_id, client_id }: { asset_id: any, client_id: string }) {
    const [data, setData] = useState<TrafficAnalyticsData | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await baseapi.get("/firewall_sections", {
                params: {
                    query: JSON.stringify({
                        ...(asset_id && client_id ? { asset_id, client_id } : {}),
                        section_name: "traffic_analytics",
                    }),
                    limit: 1,
                }
            });
            if (res.data.status && res.data.data?.[0]) {
                const raw = res.data.data[0].results;
                setData(typeof raw === "string" ? JSON.parse(raw) : raw);
            } else showError("Failed to load traffic analytics");
        } catch (e: any) { showError(e.message); }
        finally { setLoading(false); }
    }, [asset_id, client_id]);

    useEffect(() => { fetchData(); }, [fetchData]);

    if (loading) return (
        <div className="flex items-center justify-center gap-2 py-20">
            <Loader2 className="w-4 h-4 animate-spin text-blue-600 dark:text-blue-400" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-gray-500">Loading traffic analytics...</span>
        </div>
    );

    if (!data) return (
        <div className="flex flex-col items-center gap-2 py-20 opacity-40">
            <Activity size={24} className="text-slate-400 dark:text-gray-400" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-gray-500">No data available</span>
        </div>
    );

    const { quarantine_list, session_summary, traffic_counters, dos_policy_config } = data;

    return (
        <div className="space-y-6">

            {/* Top Bar */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="bg-blue-500/10 dark:bg-blue-600/20 p-1.5 rounded-lg border border-blue-500/20 dark:border-blue-500/30">
                        <Activity size={16} className="text-blue-600 dark:text-blue-400" />
                    </div>
                    <h1 className="text-lg font-bold text-slate-900 dark:text-white tracking-wide">Traffic Analytics</h1>
                </div>
                <button
                    onClick={fetchData}
                    disabled={loading}
                    className="p-1.5 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-gray-700 transition-all group shadow-sm dark:shadow-none"
                >
                    <RefreshCw size={14} className={loading ? "animate-spin" : "group-hover:text-blue-600 dark:group-hover:text-blue-400"} />
                </button>
            </div>

            {/* ── Section 1: Overview Totals ─────────────────────────────────── */}
            <div>
                <SectionHeader
                    icon={Zap}
                    title="Overview"
                    color="border-blue-500/30"
                    desc="High-level firewall activity snapshot"
                />
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    <StatCard
                        label="Active Sessions"
                        value={fmtNum(session_summary.total_sessions)}
                        icon={Users}
                        bg="bg-blue-50 dark:bg-blue-500/5"
                        border="border-blue-200 dark:border-blue-500/20"
                        text="text-blue-600 dark:text-blue-400"
                        iconBg="bg-blue-500/10 border-blue-500/20"
                        sub={session_summary.note}
                    />
                    <StatCard
                        label="Quarantined IP's"
                        value={String(quarantine_list.total)}
                        icon={Shield}
                        bg={quarantine_list.total > 0 ? "bg-rose-50 dark:bg-red-500/5" : "bg-slate-50 dark:bg-gray-700/20"}
                        border={quarantine_list.total > 0 ? "border-rose-200 dark:border-red-500/20" : "border-slate-200 dark:border-gray-600/30"}
                        text={quarantine_list.total > 0 ? "text-rose-600 dark:text-red-400" : "text-slate-500 dark:text-gray-400"}
                        iconBg={quarantine_list.total > 0 ? "bg-rose-500/10 border-rose-500/20" : "bg-slate-100 dark:bg-gray-700/30 border-slate-200 dark:border-gray-600/30"}
                        sub="IPs/users quarantined"
                    />
                    <StatCard
                        label="DoS Policies"
                        value={String(dos_policy_config.total)}
                        icon={ShieldAlert}
                        bg="bg-orange-50 dark:bg-orange-500/5"
                        border="border-orange-200 dark:border-orange-500/20"
                        text="text-orange-600 dark:text-orange-400"
                        iconBg="bg-orange-500/10 border-orange-500/20"
                        sub="Anomaly detection rules"
                    />
                </div>
            </div>

            {/* ── Section 2: Traffic Counters ────────────────────────────────── */}
            <div>
                <SectionHeader
                    icon={Activity}
                    title="Traffic Counters"
                    color="border-green-500/30"
                    desc={traffic_counters.description}
                />
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {/* Bytes */}
                    <div className="col-span-2 sm:col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <StatCard
                            label="Total Bytes"
                            value={fmtBytes(traffic_counters.total_bytes)}
                            icon={Database}
                            bg="bg-slate-50 dark:bg-gray-700/20"
                            border="border-slate-200 dark:border-gray-600/30"
                            text="text-slate-700 dark:text-gray-300"
                            iconBg="bg-slate-100 dark:bg-gray-700/30 border-slate-200 dark:border-gray-600/30"
                        />
                        <StatCard
                            label="Allowed Bytes"
                            value={fmtBytes(traffic_counters.allowed_bytes)}
                            icon={CheckCircle2}
                            bg="bg-green-50 dark:bg-green-500/5"
                            border="border-green-200 dark:border-green-500/20"
                            text="text-green-600 dark:text-green-400"
                            iconBg="bg-green-500/10 border-green-500/20"
                        />
                        <StatCard
                            label="Denied Bytes"
                            value={fmtBytes(traffic_counters.denied_bytes)}
                            icon={Ban}
                            bg="bg-rose-50 dark:bg-red-500/5"
                            border="border-rose-200 dark:border-red-500/20"
                            text="text-rose-600 dark:text-red-400"
                            iconBg="bg-rose-500/10 border-rose-500/20"
                        />
                    </div>
                    {/* Packets */}
                    <div className="col-span-2 sm:col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <StatCard
                            label="Total Packets"
                            value={fmtNum(traffic_counters.total_packets)}
                            icon={Package}
                            bg="bg-slate-50 dark:bg-gray-700/20"
                            border="border-slate-200 dark:border-gray-600/30"
                            text="text-slate-700 dark:text-gray-300"
                            iconBg="bg-slate-100 dark:bg-gray-700/30 border-slate-200 dark:border-gray-600/30"
                        />
                        <StatCard
                            label="Allowed Packets"
                            value={fmtNum(traffic_counters.allowed_packets)}
                            icon={CheckCircle2}
                            bg="bg-green-50 dark:bg-green-500/5"
                            border="border-green-200 dark:border-green-500/20"
                            text="text-green-600 dark:text-green-400"
                            iconBg="bg-green-500/10 border-green-500/20"
                        />
                        <StatCard
                            label="Denied Packets"
                            value={fmtNum(traffic_counters.denied_packets)}
                            icon={Ban}
                            bg="bg-rose-50 dark:bg-red-500/5"
                            border="border-rose-200 dark:border-red-500/20"
                            text="text-rose-600 dark:text-red-400"
                            iconBg="bg-rose-500/10 border-rose-500/20"
                        />
                    </div>
                </div>
            </div>

            {/* ── Section 3: DoS Policy Config Table ────────────────────────── */}
            <div>
                <SectionHeader
                    icon={AlertTriangle}
                    title="DoS Anomaly Detection Policies"
                    color="border-orange-500/30"
                    desc={dos_policy_config.description}
                />
                <DosPolicyTable data={dos_policy_config} />
            </div>
        </div>
    );
}
